import React, { useRef } from "react";
import { useState,useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WestIcon from "@mui/icons-material/West";
import CloseIcon from "@mui/icons-material/Close";
import { DeliveryInvoiceBatchDetails, DeliveryInvoiceSkuDetails, SalesReasonDetails } from "../../types/types";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getAllProductDetailsFromDb, getBatchDetailsFromDb, getBatchDetailsFromDbPrimary, setBatchDetailsInDb, setBatchDetailsInDbPrimary } from "../SalesReturn/SalesReturnService";
import BatchListItem from "../deliveryInvoiceEdit/BatchListItem";
import DeliveryInvoiceReasonSelectionParent from "../deliveryInvoiceEdit/DeliveryInvoiceReasonSelectionParent";
import { QuantityReason, SkuData } from "./PrimaryReturnUtils";
import PrimaryReturnBatchItem from "./PrimaryReturnBatchItem";
import PrimaryReturnSelect from "./PrimaryReturnSelect";
import "./PrimaryReturn.css"
import PrimaryReturnReasonSelectParent from "./PrimaryReturnReasonSelectParent";
import { getConfigData } from "../../services/configurationService";
import { Loader } from "../../components/loader/Loader";

export interface PrimaryReturnBatchDetails{
  batchId: string,
  rejectableCaseQty: number,
  rejectableOtherQty: number,
  rejectablePieceQty: number,
  acceptableCaseQty: number,
  acceptableOtherQty: number,
  acceptablePieceQty: number,
  rejectionReason?: string,
  reasonDetails?: Record<string, QuantityReason[]>
  otherUnitName?:string;
}

export const getLabel = (key: string) => {
    switch(key){
      case "ACCEPTED":
        return "GRN Accepted"
      case "REJECTED":
        return "GRN Rejected"
      default:
        return ""
    }
  }


const PrimaryReturnEdit: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const [expandedBatch, setExpandedBatch] = useState<PrimaryReturnBatchDetails | null>(null);
//   const { batchDetails } = useSelector((state: {collections:CollectionsState}) => state.collections);
  const showReturnQty = location.state?.showReturnQty;
  const [editedDetails, setEditedDetails] = useState<PrimaryReturnBatchDetails[]>([]);
  const selectedRowToEdit:SkuData | null = location.state?.rowData;
  const batchDetails:{[key: string]: Record<string, QuantityReason[]>} = location.state?.batchDetails;
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const { otherUnitLabel } = ConfigDictionary;
  const [disabledUoms, setDisabledUoms] = useState<string[]>([]);
  const currency = ConfigDictionary?.currency;
  const [products, setProducts] = useState<any>({});
  const [loader, setLoader] = useState(true)

  const [selectedQtyType, setSelectedQtyType] = useState<string>("REJECTED");
  const [allowedReturnQtyTypes, setAllowedReturnQtyTypes] = useState<string[]>(["ACCEPTED", "REJECTED"]);

  const fetchRequiredConfigs = async () => {
    const allowedReturnsConfig = await getConfigData("dms_configuration", "allowedReturnQtyTypes");
    const allowedReturns = allowedReturnsConfig[0]?.configValue ?? [
      "REJECTED",
    ];
    setAllowedReturnQtyTypes(allowedReturns);
    setSelectedQtyType(allowedReturns[0] || "REJECTED");
  }



  useEffect(() => {
    if(!selectedRowToEdit){
      navigate(-1);
      return;
    }

    const run = async () => { 
      setLoader(true)
      try {
      await fetchRequiredConfigs();
      const selectedBatchData:{[key:string]:Record<string, QuantityReason[]>} = batchDetails;
      let newBatchDetails:PrimaryReturnBatchDetails[] = selectedRowToEdit?.batches.map((row)=>{
        return {
          batchId: row.batchId,

          acceptableCaseQty: row.acceptedReturnableCaseQuantity ?? 0,
          acceptableOtherQty: row.acceptedReturnableOtherQuantity ?? 0,
          acceptablePieceQty: row.acceptedReturnablePieceQuantity ?? 0,

          rejectableCaseQty: row.rejectedReturnableCaseQuantity ?? 0,
          rejectableOtherQty: row.rejectedReturnableOtherQuantity ?? 0,
          rejectablePieceQty: row.rejectedReturnablePieceQuantity ?? 0,

          rejectionReason: row.grnRejectionReason ?? "",
          otherUnitName: row.otherUnitName
        };
      }) ?? [];
      newBatchDetails.forEach((batchData)=>{
        batchData.reasonDetails = selectedBatchData[batchData.batchId];
      })

      setEditedDetails(newBatchDetails);
      setExpandedBatch(newBatchDetails[0]);
      } catch(error){
        openPopup("Alert", "Something went wrong")
      } finally {
        setLoader(false)
      }
    }
    run()
  }, []);

  const handleOnClose = () => {
    navigate(-1);
  };
  
  //A map of current batch reasons being edited, if a reason has error then we will add eky error in its QuantityReason object

  const [errorObj, setErrorObj] = useState<Record<string, Record<string, QuantityReason[]>>>({});

  const calculateError = () => {

    setErrorObj((prevState) => {
      const updatedErrorObj: Record<string, Record<string, QuantityReason[]>> = { ...prevState };
      
      editedDetails.forEach((batch) => {
        const reasons: Record<string, QuantityReason[]> = batch.reasonDetails || {};
        const updatedReasonDetails: Record<string, QuantityReason[]> = {};

        Object.entries(reasons).forEach(([category, list]) => {
          if (!Array.isArray(list)) {
            updatedReasonDetails[category] = [];
            return;
          }
          updatedReasonDetails[category] = list.map((item) => {
            const qty =
              (item.caseQty || 0) +
              (item.otherQty || 0) +
              (item.pieceQty || 0);

            const hasReason =
              item.reason && item.reason.toString().trim() !== "";

            const newItem: QuantityReason = { ...item };

            // CASE 1: No quantity but reason exists → quantityError
            if (qty === 0 && hasReason && category === "ACCEPTED") {
              newItem.quantityError = true;
            } else {
              newItem.quantityError = false;
            }

            // CASE 2: Quantity exists but no reason → reasonError
            if (qty > 0 && !hasReason) {
              newItem.reasonError = true;
            } else {
              newItem.reasonError = false;
            }          
            return newItem;
          });
        });
        updatedErrorObj[batch.batchId] = updatedReasonDetails;
      });

      return updatedErrorObj;
    });
  }

const filterReasonDetails = (batch: any) : Record<string, QuantityReason[]> => {
  const filteredMap: Record<string, QuantityReason[]> = {};
  const reasons: Record<string, QuantityReason[]> = batch.reasonDetails || {};
  calculateError();

  Object.entries(reasons).forEach(([category, list]) => {
    if (!Array.isArray(list)) {
      // safety: skip non-arrays
      filteredMap[category] = [];
      return;
    }

    const kept: QuantityReason[] = [];

    for (const item of list) {
      const qty = (item.caseQty || 0) + (item.otherQty || 0) + (item.pieceQty || 0);

      if (qty > 0) {
        if (item.reason && item.reason.toString().trim() !== "") {
          // keep item (make a shallow clone to avoid accidental shared refs)
          kept.push({ ...item });
        } else {
          openPopup("Error", "Please add reasons to proceed");
          throw Error("reason details not added");
        }
      }
      if (qty === 0){
        if (item.reason && item.reason.toString().trim() !== "" && category === "ACCEPTED"){
        
          openPopup("Error", "Please enter quantity to proceed")
          throw Error("quantity not added")
        } else{
          kept.push({...item})
        }
      }
    }

    filteredMap[category] = kept;
  });

  return filteredMap;
};


  const handleOnSubmit = async() => {

    try {

    const newBatchDetails = (await getBatchDetailsFromDbPrimary("primaryReturn")) ?? {};
    if(selectedRowToEdit!=null){
      newBatchDetails[selectedRowToEdit.skuCode] = newBatchDetails[selectedRowToEdit.skuCode] ?? {};
      editedDetails.forEach(batch=>{
        const reasons = batch.reasonDetails ?? {};
        const filteredReasons = groupByReason(filterReasonDetails(batch));
        newBatchDetails[selectedRowToEdit.skuCode][batch.batchId] = filteredReasons;
      })
      await setBatchDetailsInDbPrimary(newBatchDetails, "primaryReturn");
    }
    handleOnClose()
    } catch (error) {
    }
  }

function groupByReason(
  reasonMap: Record<string, QuantityReason[]>
): Record<string, QuantityReason[]> {
  const groupedResult: Record<string, QuantityReason[]> = {};

  Object.entries(reasonMap).forEach(([category, items]) => {
    const groupedMap = items.reduce((acc, current) => {
      if (!acc[current.reason]) {
        acc[current.reason] = {
          reason: current.reason,
          caseQty: 0,
          pieceQty: 0,
          otherQty: 0,
        };
      }

      acc[current.reason].caseQty += current.caseQty || 0;
      acc[current.reason].pieceQty += current.pieceQty || 0;
      acc[current.reason].otherQty += current.otherQty || 0;

      return acc;
    }, {} as Record<string, QuantityReason>);

    groupedResult[category] = Object.values(groupedMap);
  });

  return groupedResult;
}


  const handleReset = () => {
    let newBatchDetails:PrimaryReturnBatchDetails[] = selectedRowToEdit?.batches.map((row)=>{
      return {
        batchId: row.batchId,

        acceptableCaseQty: row.acceptedReturnableCaseQuantity ?? 0,
        acceptableOtherQty: row.acceptedReturnableOtherQuantity ?? 0,
        acceptablePieceQty: row.acceptedReturnablePieceQuantity ?? 0,

        rejectableCaseQty: row.rejectedReturnableCaseQuantity ?? 0,
        rejectableOtherQty: row.rejectedReturnableOtherQuantity ?? 0,
        rejectablePieceQty: row.rejectedReturnablePieceQuantity ?? 0,
        rejectionReason: row.grnRejectionReason ?? "",
        otherUnitName: row.otherUnitName
      };
    }) ?? [];
    setEditedDetails(newBatchDetails);
    setSelectedQtyType(allowedReturnQtyTypes[0] || "REJECTED");
    setExpandedBatch(null);
  };

  useEffect(() => {
    const run = async () => {
      const productDetails = await getAllProductDetailsFromDb();
      setProducts(productDetails[selectedRowToEdit?.batchCode ?? ""]
)
    }
    run()
  }, [])

  const handleChangeExpandedBatch = async (batch: PrimaryReturnBatchDetails) => {
    setExpandedBatch((prevState: PrimaryReturnBatchDetails | null) =>
      prevState?.batchId === batch?.batchId ? null : batch
    );
  };

  const handleChangeQtyType = (type: string) => {
    setSelectedQtyType(type);
  }
  useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        if (selectedRowToEdit) {
          try {
            handleOnSubmit();
          } catch (e) {
            // Error handled by filterReasonDetails via openPopup
          }
        }
      } else if ((event.ctrlKey || event.metaKey) && (event.key === 'x' || event.key === 'z')) {
        event.preventDefault();
        handleReset();
      }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [selectedRowToEdit, handleOnSubmit, handleReset]);


  return (
    loader ? 
    <Loader/>
    :
    (
      <div className="edit-return-container">
        <header style={{height: "min-content", padding: "16px 20px"}}>
          <div>
            <WestIcon style={{ cursor: "pointer" }} onClick={() => navigate(-1)} />
            <p> Add Return Qty for {selectedRowToEdit?.skuName} ({selectedRowToEdit?.batchCode}) </p>
          </div>
          <CloseIcon style={{ cursor: "pointer" }} onClick={handleOnClose} />
        </header>
        <div className="edit-return-model-container" style={{height: "100%", overflow: "hidden"}}>
          <div className="inside-header" style={{alignItems: "center"}}>
              <div className="inside-header-container">
                  <div className="inside-header-label">SKU Code</div>
                  <div className="inside-header-value">{selectedRowToEdit?.batchCode}</div>
              </div>
              <div className="inside-header-container">
                  <div className="inside-header-label">SKU Name</div>
                  <div className="inside-header-value">{selectedRowToEdit?.skuName}</div>
              </div>
          </div>
          <div className="inside-body" style={{gap: "15px", padding: "15px", paddingBottom: "0px", height: "100%", overflow: "hidden"}}>
              <div className="batch-id-text-delivery" style={{margin:"0px"}}>
                  Batch ID
              </div>
              <div className="edit-left" style={{width: "100%"}}>
                  <div className="batches-list-container-delivery" style={{height: "100%", overflow: "hidden"}}>
                      <div className="batch-list-heading">
                          <span>Select a batch ID to add damage quantity</span>
                      </div>
                      <div className="batches-list-delivery" style={{scrollbarWidth: "thin"}}>
                        {editedDetails.length ? editedDetails.map((batch) => {
                          return (
                              <PrimaryReturnBatchItem
                                  key={batch.batchId}
                                  batch={batch}
                                  isSelected={expandedBatch?.batchId === batch.batchId}
                                  onSelect={() => {
                                    handleChangeExpandedBatch(batch)
                                  }
                                  }
                                  allowedReturnQtyTypes={allowedReturnQtyTypes}
                              />
                          );
                        }):
                        <div className="batch-list-heading center">
                            <span>No Batch ID</span>
                        </div>
                        }
                      </div>
                  </div>
              </div>
              {true && (
              <div className="edit-right-delivery" style={{width: "100%", visibility: expandedBatch?.batchId ? "visible" : "hidden"}}>
              <div className="edit-right-delivery-top" style={{height: "100%"}}>
                <div className="edit-right-delivery-top-header">
                  <div style={{display: "flex", gap: "16px"}}>
                    <p className="batch-id-nos-delivery">{expandedBatch?.batchId}</p>
                  </div>
                </div>
                <div className="batch-ids-container-delivery" style={{paddingBottom: "20px"}}>
                  
                  <div className="reasons-parent-delivery" style={{display: "flex", flexDirection: "column", gap: "10px", padding: "10px", height: "100%"}}>
                    <div className="qty-type" style={{margin: "0px"}}>
                      {allowedReturnQtyTypes.map((key: string) => (
                        <PrimaryReturnSelect
                          key={key}
                          label={getLabel(key)}
                          isSelected={selectedQtyType === key}
                          onSelect={() => handleChangeQtyType(key)}
                        />
                      ))}
                    </div>
                    {/* <DeliveryInvoiceReasonSelectionParent batchData={expandedBatch} /> */}
                    {
                      expandedBatch?.batchId && products &&
                      <PrimaryReturnReasonSelectParent errorObj={errorObj[expandedBatch.batchId]} batchData={expandedBatch} selectedQuantity={selectedQtyType} productDetails={  products }/>
                    }
                  </div>
                </div>
              </div>
            </div>
              )}
          </div>
        </div>
        <footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 15px" }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center", fontSize: "12px" }}>
            {allowedReturnQtyTypes.includes("ACCEPTED") &&
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: "#6fe2c2"
                }}></span>
                <span style={{ fontWeight: 500 }}>GRN Accepted</span>
              </span>
            }

            {allowedReturnQtyTypes.includes("REJECTED") &&
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: "#E87474"
                }}></span>
                <span style={{ fontWeight: 500 }}>GRN Rejected</span>
              </span>
            }
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button className="button-reset-delivery" onClick={handleReset}>
              <span className="button-reset-delivery-text">Reset</span>
            </button>
            <button className="button-save-delivery" onClick={handleOnSubmit}>
              <span className="button-save-delivery-text">Submit</span>
            </button>
          </div>
        </footer>
      </div>
    )
  );
};

export default PrimaryReturnEdit;
